/**
 * REALTIME HUB (REALTIME-HUB.md P0+P1) — the client reconcile loop.
 *
 * THE INVARIANT: the client's truth is a fetchable SNAPSHOT; a realtime event is only an invalidation
 * signal. This hook fetches the authoritative room snapshot and RECONCILES it — on mount, on a signal
 * bump (an agent/run beacon or the payload-free ROOM_STATE_CHANGED arrived), on tab-focus, and on a
 * heartbeat while a run is active. It never builds state from the event stream, so a missed / early /
 * reordered / dropped beacon can't corrupt the agent tree — the next reconcile heals it.
 *
 * P1: (a) DEBOUNCED + JITTERED reconcile — a burst of signals coalesces into ONE fetch and a fleet of
 * clients doesn't stampede the endpoint on the same beacon (§4.1 thundering herd); (b) the fetch sends
 * ``since=lastSeq`` — a settled, unchanged room answers ``{seq, notModified}`` (tiny body, no verify)
 * and the hook keeps its state (§4.1 If-None-Match fast-path; the server NEVER 304s a live run, so the
 * SFN-verify heal is preserved).
 *
 * Returns ``statusByAgent`` (authoritative per-agent status) that the room OVERLAYS onto its
 * stream-derived roster — structure/labels from the manifest, STATUS from the snapshot.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchRoomState } from '../services/dataPlaneApi';

export interface RoomSnapshot {
  seq: number;
  run: { runId: string; status: string; uiState?: string };
  agents: Array<{ agentId: string; status: string; nodePath?: string; usage?: unknown }>;
  approvals?: Array<Record<string, unknown>>;
  builderWaits?: Array<Record<string, unknown>>;
  plan?: Record<string, unknown>;
}

// Debounce base + jitter ceiling (ms). Small enough to feel live, big enough to coalesce an
// AGENT_STARTED/ROOM_STATE_CHANGED burst into one round-trip and de-sync a client fleet.
const DEBOUNCE_MS = 80;
const JITTER_MS = 170;

export function useRoomState(
  networkId: string | null | undefined,
  poolId: string | null | undefined,
  opts: { active?: boolean; signal?: number | string; heartbeatMs?: number } = {},
): {
  snapshot: RoomSnapshot | null;
  statusByAgent: Record<string, string>;
  run: RoomSnapshot['run'] | null;
  uiState: string | null;
  approvals: Array<Record<string, unknown>>;
  builderWaits: Array<Record<string, unknown>>;
} {
  const { active = false, signal = 0, heartbeatMs = 5000 } = opts;
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const lastSeqRef = useRef<number>(-1);
  const lastRunIdRef = useRef<string>('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reconcile = useCallback(async () => {
    if (!networkId) return;
    try {
      const since = lastSeqRef.current > 0 ? lastSeqRef.current : undefined;
      const snap = (await fetchRoomState(networkId, { pool: poolId || undefined, since })) as
        RoomSnapshot & { notModified?: boolean };
      if (!snap || typeof snap.seq !== 'number') return;
      if (snap.notModified) return;                 // 304 fast-path — nothing changed, keep state
      const runId = snap.run?.runId || '';
      // FORWARD-ONLY apply (§0.2): accept only a >= seq for the SAME run; a NEW run resets the counter,
      // so a runId change always accepts + rebaselines (else a fresh run's low seq would be rejected).
      const newRun = runId !== lastRunIdRef.current;
      if (newRun || snap.seq >= lastSeqRef.current) {
        lastRunIdRef.current = runId;
        lastSeqRef.current = snap.seq;
        setSnapshot(snap);
      }
    } catch {
      /* best-effort — the next reconcile (focus/heartbeat/signal) heals */
    }
  }, [networkId, poolId]);

  // COALESCED trigger (P1): one pending fetch per burst, trailing-edge, with per-client jitter.
  const schedule = useCallback(() => {
    if (timerRef.current) return;                   // a fetch is already pending — coalesce
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      reconcile();
    }, DEBOUNCE_MS + Math.floor(Math.random() * JITTER_MS));
  }, [reconcile]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // Mount + every signal bump (a beacon arrived → reconcile; never trust its payload as state).
  useEffect(() => { schedule(); }, [schedule, signal]);

  // Tab-focus — a backgrounded tab that missed events self-heals the moment the user returns.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onFocus = () => schedule();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [schedule]);

  // AUTHORITATIVE liveness: keep reconciling while EITHER the caller says active (its ``uiState``) OR
  // our OWN last snapshot says ``run.status==='running'``. This breaks a vicious circle that caused
  // the frank-beaver out-of-sync (LESSONS L18): a worker child's stray UI_STATE=IDLE flipped the
  // caller's ``uiState`` off mid-run → ``active`` went false → the heartbeat that would re-read the
  // (still-``running``) snapshot stopped → the tree froze ~6 min behind the truth. Gating on the
  // snapshot's own run status (which the caller cannot corrupt) makes a mis-flip self-heal. The mount
  // + signal reconciles still fire regardless of ``active``, so the FIRST fetch always learns the run
  // is live even if the caller handed us ``active=false``.
  const snapRunning = String(snapshot?.run?.status || '').toLowerCase() === 'running';
  const effectiveActive = active || snapRunning;
  useEffect(() => {
    if (!effectiveActive) return undefined;
    const id = setInterval(schedule, heartbeatMs);
    return () => clearInterval(id);
  }, [effectiveActive, heartbeatMs, schedule]);

  const statusByAgent: Record<string, string> = {};
  for (const a of snapshot?.agents || []) {
    if (a && a.agentId) statusByAgent[a.agentId] = a.status;
  }
  // v2 (LIVE_STATE_FROM_SNAPSHOT): the LIVE room state the client must source from the snapshot, not
  // the event stream — run uiState + pending approvals + builder waits (in addition to statusByAgent).
  return {
    snapshot,
    statusByAgent,
    run: snapshot?.run || null,
    uiState: snapshot?.run?.uiState || null,
    approvals: snapshot?.approvals || [],
    builderWaits: snapshot?.builderWaits || [],
  };
}
